/**
 * Shared component display-title helpers for Master Template Editor flows.
 *
 * Components are identified by UUID `id`. Duplicate display titles are allowed.
 * Use these helpers from Add, rename, and inspector validation — not in UI components directly.
 */
import { writeWebsiteTemplateComponentNow } from "./websiteTemplateComponentSession";
import {
  getCarouselTitleLengthError,
  isTitleFieldError,
  MAX_CAROUSEL_TITLE_CHARS,
  trimCarouselTitle,
  validateTitleForEdit,
} from "../../../Utils/helpers/carouselHelper";

export const COMPONENT_TITLE_REQUIRED_ERROR = "A title is required.";

/** Leave-dialog Add/Update when the component title is still empty. */
export const COMPONENT_TITLE_BEFORE_LEAVE_ERROR =
  "A title is required to add/update this component.";

/** @deprecated Duplicate titles are allowed; kept for legacy error string comparisons. */
export const DUPLICATE_COMPONENT_TITLE_ERROR =
  "A component with this title already exists.";

export {
  MAX_CAROUSEL_TITLE_CHARS,
  trimCarouselTitle,
  getCarouselTitleLengthError,
  isTitleFieldError,
  validateTitleForEdit,
};

/** Trim user-entered component / section title. */
export function normalizeComponentTitle(value: unknown): string {
  return trimCarouselTitle(String(value ?? ""));
}

export type ComponentTitleResult =
  | { ok: true; name: string }
  | { ok: false; error: string };

type ValidateWebsiteComponentTitleOpts = {
  /** Reserved-title context for content carousel edits. */
  reservedContext?: Parameters<typeof validateTitleForEdit>[1];
  /** When true, apply content-carousel reserved title rules. */
  checkReserved?: boolean;
};

/**
 * Validate a title for insert or rename: required, max length, reserved names.
 * Does not check duplicate titles.
 */
export function validateWebsiteComponentTitle(
  value: unknown,
  opts?: ValidateWebsiteComponentTitleOpts,
): ComponentTitleResult {
  const name = normalizeComponentTitle(value);
  if (!name) {
    return { ok: false, error: COMPONENT_TITLE_REQUIRED_ERROR };
  }
  const lengthError = getCarouselTitleLengthError(name);
  if (lengthError) {
    return { ok: false, error: lengthError };
  }
  if (opts?.checkReserved) {
    const reserved = validateTitleForEdit(name, opts.reservedContext, true);
    if (!reserved.isValid) {
      return {
        ok: false,
        error: reserved.error || "Invalid title.",
      };
    }
  }
  return { ok: true, name };
}

/** Resolve the title stored on a new component row. */
export function resolveWebsiteComponentInsertTitle(
  desired: unknown,
  opts?: Omit<ValidateWebsiteComponentTitleOpts, "checkReserved">,
): ComponentTitleResult {
  return validateWebsiteComponentTitle(desired, opts);
}

/** Resolve a renamed component title before persisting by row `id`. */
export function resolveWebsiteComponentRenameTitle(
  desired: unknown,
  currentTitle?: string,
  opts?: ValidateWebsiteComponentTitleOpts,
): ComponentTitleResult {
  const next = normalizeComponentTitle(desired);
  const current = normalizeComponentTitle(currentTitle);
  if (current && current.toLowerCase() === next.toLowerCase()) {
    return next ? { ok: true, name: next } : { ok: false, error: COMPONENT_TITLE_REQUIRED_ERROR };
  }
  return validateWebsiteComponentTitle(desired, opts);
}

function titlesMatch(a: string, b: string): boolean {
  return (
    normalizeComponentTitle(a).toLowerCase() ===
    normalizeComponentTitle(b).toLowerCase()
  );
}

/** Rename a website `template_components` row by `id`. */
export async function renameWebsiteTemplateComponent(opts: {
  userId: string;
  rowId: string;
  nextName: string;
  currentName?: string;
}): Promise<{ ok: boolean; error?: string }> {
  const userId = String(opts.userId || "").trim();
  const rowId = String(opts.rowId || "").trim();
  const resolved = resolveWebsiteComponentRenameTitle(
    opts.nextName,
    opts.currentName,
  );
  if (!resolved.ok) {
    return { ok: false, error: resolved.error };
  }
  if (!userId || !rowId) {
    return { ok: false, error: COMPONENT_TITLE_REQUIRED_ERROR };
  }
  const current = normalizeComponentTitle(opts.currentName);
  if (current && titlesMatch(current, resolved.name)) {
    return { ok: true };
  }
  const wrote = await writeWebsiteTemplateComponentNow(userId, rowId, {
    name: resolved.name,
  });
  if (!wrote) {
    return { ok: false, error: "Couldn't update that title." };
  }
  return { ok: true };
}
